import React, { useRef, useState, useEffect, useCallback } from "react";
import { ArrowLeft, Maximize2, Minimize2, Play, Pause, Rewind, FastForward, SkipBack, SkipForward, Flag, Scissors, ZoomIn, ZoomOut, CheckCircle2 } from "lucide-react";

const formatTime = (s) => {
  if (!s || isNaN(s)) return "0:00";
  const m = Math.floor(s / 60);
  const sec = Math.floor(s % 60);
  return `${m}:${sec.toString().padStart(2, "0")}`;
};

// IMPORTANT: the <video> element is rendered at exactly ONE fixed location in
// the JSX tree (inside `videoHostRef`). Both portrait and fullscreen UIs live
// AROUND this single video DOM node — they never re-parent it. This is what
// preserves playback state, currentTime, and the progress bar across
// orientation/fullscreen toggles. Earlier attempts referenced the same JSX
// expression in two conditional branches; React still creates two DOM nodes
// in that case, which was causing the "reset to 0:00" bug.
const VideoPlayer = ({ game, onBack, onCreateClip }) => {
  const videoRef = useRef(null);
  const containerRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [clipStart, setClipStart] = useState(null);
  const [clipEnd, setClipEnd] = useState(null);
  const [showClipSave, setShowClipSave] = useState(false);
  const [clipTitle, setClipTitle] = useState("");
  const [zoom, setZoom] = useState(1);
  const [showControls, setShowControls] = useState(true);
  const [videoError, setVideoError] = useState(false);
  const [toast, setToast] = useState(null);
  const hideTimer = useRef(null);
  const toastTimer = useRef(null);
  const seekingRef = useRef(false);
  // When the scrubber drags PAST a segment boundary we trigger a segment switch
  // and remember where to seek inside the new segment once it's loaded.
  const pendingSeekRef = useRef(null);

  // ── Multi-part segment support ──
  // Treat every game as having a `segments` array. Single-file games get a
  // synthesised one-element array so the rest of the player code stays uniform.
  const segments = React.useMemo(() => {
    const real = (game && Array.isArray(game.segments) && game.segments.length > 0) ? game.segments : null;
    if (real) {
      // Sort by part_index for stable order
      return [...real].sort((a, b) => (a.part_index ?? 0) - (b.part_index ?? 0));
    }
    if (game?.video_url) {
      return [{ id: "single", label: game.title || "Full Game", part_index: 0, video_url: game.video_url, duration_seconds: game.duration_seconds || 0 }];
    }
    return [];
  }, [game]);
  const isMultiSegment = segments.length > 1;
  const [segmentIdx, setSegmentIdx] = useState(0);
  // ── Cumulative time across segments ──
  // For multi-part games the user wants ONE continuous timeline: if Q1 is 10:00,
  // then Q2's local 0:00 displays as 10:00. We compute cumulative offsets for
  // each segment and use them everywhere a time label is shown OR the scrub bar
  // is painted. The video element itself still plays one segment at a time
  // (currentTime resets to 0 between segments) — we just translate.
  const segmentOffsets = React.useMemo(() => {
    let acc = 0;
    return segments.map((s) => {
      const off = acc;
      acc += (s.duration_seconds || 0);
      return off;
    });
  }, [segments]);
  const cumulativeDuration = React.useMemo(
    () => segments.reduce((a, s) => a + (s.duration_seconds || 0), 0),
    [segments]
  );
  const currentOffset = segmentOffsets[segmentIdx] || 0;
  // When the user uses tab-clicks we want to remember if they were playing,
  // so we can auto-resume on the next segment.
  const wasPlayingRef = useRef(false);
  const currentSegment = segments[segmentIdx] || null;
  const currentVideoUrl = currentSegment?.video_url || game?.video_url || "";
  const currentSegmentId = currentSegment?.id || currentSegment?.segment_id || null;

  const showToast = useCallback((msg, color) => {
    setToast({ msg, color });
    if (toastTimer.current) clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(() => setToast(null), 1800);
  }, []);

  const resetHideTimer = useCallback(() => {
    setShowControls(true);
    if (hideTimer.current) clearTimeout(hideTimer.current);
    if (playing) hideTimer.current = setTimeout(() => setShowControls(false), 4000);
  }, [playing]);

  const fillRef = useRef(null);       // imperatively updated <div> for the bar fill
  const thumbRef = useRef(null);      // imperatively updated thumb
  const fsFillRef = useRef(null);     // same for fullscreen progress bar
  const fsThumbRef = useRef(null);
  const timeLabelRef = useRef(null);  // imperatively updated time text
  const fsTimeLabelRef = useRef(null);

  // Direct-DOM bar updater — bypasses React render cycle entirely, so a 60fps
  // video frame loop updates instantly without re-rendering the whole player.
  // This is what kills the "glitch" — the previous setState approach forced
  // React to reconcile every frame.
  //
  // For multi-part games we translate the local segment time to cumulative
  // time using `currentOffset` so the user always sees one continuous timeline.
  const paintBar = useCallback((localT, localDur) => {
    // Cumulative position across the whole game
    const t = (currentOffset || 0) + (localT || 0);
    // Use cumulativeDuration if we have one (real multi-part); otherwise fall
    // back to the local segment duration — single-part games still work.
    const dur = cumulativeDuration > 0 ? cumulativeDuration : (localDur || 0);
    const pct = (dur > 0 && isFinite(dur)) ? Math.max(0, Math.min(1, t / dur)) * 100 : 0;
    const pctStr = `${pct}%`;
    if (fillRef.current) fillRef.current.style.width = pctStr;
    if (thumbRef.current) thumbRef.current.style.left = pctStr;
    if (fsFillRef.current) fsFillRef.current.style.width = pctStr;
    if (fsThumbRef.current) fsThumbRef.current.style.left = pctStr;
    const label = formatTime(t);
    if (timeLabelRef.current && timeLabelRef.current.textContent !== label) {
      timeLabelRef.current.textContent = label;
    }
    if (fsTimeLabelRef.current && fsTimeLabelRef.current.textContent !== label) {
      fsTimeLabelRef.current.textContent = label;
    }
  }, [currentOffset, cumulativeDuration]);

  // Frame-accurate playback updates. Uses requestVideoFrameCallback (the API
  // designed for exactly this) when available, falls back to requestAnimationFrame
  // on older browsers. Either way, all visual updates happen via direct DOM
  // mutation (paintBar) so React never re-renders during normal playback.
  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    let rafId = 0;
    let rvfcId = 0;
    let cancelled = false;

    const useRVFC = typeof v.requestVideoFrameCallback === "function";

    const onFrame = () => {
      if (cancelled || !v) return;
      paintBar(v.currentTime, v.duration);
      // Capture duration into React state once it's stable so the right-side
      // total label can render — but we DON'T re-paint the bar from React.
      if (!isNaN(v.duration) && isFinite(v.duration)) {
        setDuration(prev => (Math.abs(v.duration - prev) > 0.5 ? v.duration : prev));
      }
      if (useRVFC) {
        rvfcId = v.requestVideoFrameCallback(onFrame);
      } else {
        rafId = requestAnimationFrame(onFrame);
      }
    };
    if (useRVFC) {
      rvfcId = v.requestVideoFrameCallback(onFrame);
    } else {
      rafId = requestAnimationFrame(onFrame);
    }

    // Even when the video is PAUSED (no rVFC ticks), we still want the bar
    // to reflect any seek the user does. Do a low-rate poll for that.
    const pollId = setInterval(() => {
      if (v.paused) paintBar(v.currentTime, v.duration);
    }, 100);

    const onPlay = () => setPlaying(true);
    const onPause = () => setPlaying(false);
    const onEnd = () => setPlaying(false);
    const onLoaded = () => paintBar(v.currentTime, v.duration);
    v.addEventListener("play", onPlay);
    v.addEventListener("pause", onPause);
    v.addEventListener("ended", onEnd);
    v.addEventListener("loadedmetadata", onLoaded);
    v.addEventListener("seeked", onLoaded);
    return () => {
      cancelled = true;
      cancelAnimationFrame(rafId);
      if (useRVFC && rvfcId && typeof v.cancelVideoFrameCallback === "function") {
        try { v.cancelVideoFrameCallback(rvfcId); } catch { /* */ }
      }
      clearInterval(pollId);
      v.removeEventListener("play", onPlay);
      v.removeEventListener("pause", onPause);
      v.removeEventListener("ended", onEnd);
      v.removeEventListener("loadedmetadata", onLoaded);
      v.removeEventListener("seeked", onLoaded);
    };
  }, [paintBar]);

  useEffect(() => {
    const onFSChange = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener("fullscreenchange", onFSChange);
    const onEsc = (e) => { if (e.key === "Escape" && isFullscreen) setIsFullscreen(false); };
    document.addEventListener("keydown", onEsc);
    return () => {
      document.removeEventListener("fullscreenchange", onFSChange);
      document.removeEventListener("keydown", onEsc);
    };
  }, [isFullscreen]);

  // When the segment index changes (user clicked a tab OR auto-advance fired),
  // reload the video element with the new source and resume playback if the
  // previous segment was playing. This keeps the SAME <video> DOM node — only
  // the underlying source URL changes — so refs, fullscreen, and orientation
  // state all survive the switch.
  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    v.load();
    setCurrentTime(0);
    // If the scrubber landed us here, apply the saved local-time seek as soon
    // as the new metadata is ready.
    const pending = pendingSeekRef.current;
    if (pending !== null && pending !== undefined) {
      pendingSeekRef.current = null;
      const seekWhenReady = () => {
        try { v.currentTime = Math.max(0, pending); } catch { /* */ }
        paintBar(pending, v.duration);
        v.removeEventListener("loadedmetadata", seekWhenReady);
      };
      v.addEventListener("loadedmetadata", seekWhenReady);
    } else {
      paintBar(0, currentSegment?.duration_seconds || 0);
    }
    if (wasPlayingRef.current) {
      const p = v.play();
      if (p && typeof p.then === "function") p.catch(() => {});
      wasPlayingRef.current = false;
    }
    // Reset clip flags when switching segments — clipStart/clipEnd are
    // recorded as segment-local times and only make sense within one segment.
    // Users see the cumulative-time UI but clips are still tied to a single segment.
    setClipStart(null);
    setClipEnd(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [segmentIdx, currentVideoUrl]);

  const switchToSegment = useCallback((idx) => {
    if (idx < 0 || idx >= segments.length || idx === segmentIdx) return;
    wasPlayingRef.current = !videoRef.current?.paused;
    setSegmentIdx(idx);
  }, [segmentIdx, segments.length]);

  const togglePlay = () => {
    const v = videoRef.current;
    if (!v) return;
    if (v.paused) {
      const p = v.play();
      if (p && typeof p.then === "function") p.catch(() => {});
      // optimistic flip — the `play` event will confirm but this avoids the
      // ~150 ms latency before the icon updates on slow devices.
      setPlaying(true);
    } else {
      v.pause();
      setPlaying(false);
    }
    resetHideTimer();
  };

  const skip = (sec) => {
    const v = videoRef.current;
    if (!v) return;
    const t = Math.max(0, Math.min(duration || v.duration || 0, v.currentTime + sec));
    v.currentTime = t;
    setCurrentTime(t);
    resetHideTimer();
  };

  // Pointer-based draggable scrubber. While dragging, paintBar is called
  // directly so the bar tracks the finger with zero React render cost.
  // For multi-part games the bar represents the cumulative timeline. Dragging
  // past the end of the current segment switches to the next segment + seeks
  // to the correct local offset.
  const onBarPointerDown = (e) => {
    e.preventDefault();
    const bar = e.currentTarget;
    bar.setPointerCapture?.(e.pointerId);
    seekingRef.current = true;
    const wasPlaying = !videoRef.current?.paused;
    if (wasPlaying) videoRef.current.pause();
    const move = (clientX) => {
      const r = bar.getBoundingClientRect();
      const pct = Math.max(0, Math.min(1, (clientX - r.left) / r.width));
      const v = videoRef.current;
      // Use cumulative duration when we have multi-part data; fall back to
      // the local video element duration for single-part games.
      const totalDur = cumulativeDuration > 0 ? cumulativeDuration : (v?.duration || duration || 0);
      if (!totalDur || isNaN(totalDur) || !isFinite(totalDur)) return;
      const cumT = pct * totalDur;
      // Find which segment that cumulative time lands in
      let targetIdx = 0;
      let targetLocalT = cumT;
      for (let i = 0; i < segments.length; i++) {
        const segDur = segments[i].duration_seconds || 0;
        if (cumT < segmentOffsets[i] + segDur || i === segments.length - 1) {
          targetIdx = i;
          targetLocalT = Math.max(0, cumT - segmentOffsets[i]);
          break;
        }
      }
      if (targetIdx !== segmentIdx) {
        // Switch segments mid-drag — remember playing state so the new
        // segment auto-resumes. After switch, the useEffect will reload the
        // video; seek to targetLocalT once it's ready.
        wasPlayingRef.current = wasPlaying;
        pendingSeekRef.current = targetLocalT;
        setSegmentIdx(targetIdx);
        paintBar(targetLocalT, segments[targetIdx]?.duration_seconds || 0);
      } else if (v) {
        v.currentTime = targetLocalT;
        paintBar(targetLocalT, v.duration);
      }
    };
    move(e.clientX);
    const onMove = (ev) => move(ev.clientX);
    const onUp = () => {
      bar.removeEventListener("pointermove", onMove);
      bar.removeEventListener("pointerup", onUp);
      bar.removeEventListener("pointercancel", onUp);
      seekingRef.current = false;
      if (wasPlaying) videoRef.current?.play().catch(() => {});
      resetHideTimer();
    };
    bar.addEventListener("pointermove", onMove);
    bar.addEventListener("pointerup", onUp);
    bar.addEventListener("pointercancel", onUp);
    resetHideTimer();
  };

  const toggleFullscreen = () => {
    const el = containerRef.current;
    if (!el) return;
    if (isFullscreen) {
      if (document.fullscreenElement) {
        try { document.exitFullscreen?.()?.catch?.(() => {}); } catch { /* */ }
      }
      setIsFullscreen(false);
    } else {
      try { el.requestFullscreen?.()?.catch?.(() => {}); } catch { /* */ }
      setIsFullscreen(true);
    }
  };

  const setClipStartMark = () => {
    const t = videoRef.current?.currentTime ?? currentTime;
    setClipStart(t);
    setCurrentTime(t); // keep React state somewhat in sync for the markers
    showToast(`Clip start at ${formatTime((currentOffset || 0) + t)}`, "#4caf50");
    resetHideTimer();
  };
  const setClipEndMark = () => {
    const t = videoRef.current?.currentTime ?? currentTime;
    setClipEnd(t);
    setCurrentTime(t);
    setShowClipSave(true);
    showToast(`Clip end at ${formatTime((currentOffset || 0) + t)}`, "#f44336");
    resetHideTimer();
  };

  const saveClip = () => {
    if (clipTitle && clipStart !== null && clipEnd !== null && onCreateClip) {
      // For multi-part games we pass segment_id so the backend stores the
      // right video_url. start_time/end_time remain LOCAL to the segment so
      // ClipPlayer can seek inside that segment without any translation.
      onCreateClip(clipTitle, clipStart, clipEnd, {
        segment_id: currentSegmentId,
        segment_label: currentSegment?.label,
        cumulative_start: (currentOffset || 0) + clipStart,
        cumulative_end: (currentOffset || 0) + clipEnd,
      });
      setClipStart(null); setClipEnd(null); setClipTitle(""); setShowClipSave(false);
    }
  };

  const zoomIn = () => setZoom(z => Math.min(3, z + 0.25));
  const zoomOut = () => setZoom(z => Math.max(1, z - 0.25));

  const progress = duration > 0 ? (currentTime / duration) * 100 : 0;
  const isCssFullscreen = isFullscreen && !document.fullscreenElement;

  // Bar — uses imperative refs for the orange fill, thumb, and time label so
  // updates from rVFC don't re-render the React tree. Static markers (clip
  // start/end) and the track styling are static React.
  const ProgressBar = ({ height = 4, thumbSize = 14, hitArea = 16, fillRefHook, thumbRefHook }) => (
    <div
      data-testid="progress-bar"
      onPointerDown={onBarPointerDown}
      style={{
        flex: 1, height: hitArea, display: "flex", alignItems: "center",
        cursor: "pointer", position: "relative", touchAction: "none",
      }}
    >
      <div style={{ position: "absolute", left: 0, right: 0, height, background: "#333", borderRadius: height / 2 }} />
      <div ref={fillRefHook} style={{ position: "absolute", left: 0, height, width: "0%", background: "#ff6b00", borderRadius: height / 2, willChange: "width" }} />
      <div ref={thumbRefHook} style={{
        position: "absolute", left: "0%", top: "50%",
        width: thumbSize, height: thumbSize, borderRadius: "50%", background: "#ff6b00",
        transform: "translate(-50%, -50%)", boxShadow: "0 0 0 2px rgba(0,0,0,0.4)",
        pointerEvents: "none", willChange: "left",
      }} />
      {clipStart !== null && (
        <div style={{ position: "absolute", left: `${(((currentOffset || 0) + clipStart) / (cumulativeDuration || duration || 1)) * 100}%`, top: 0, width: 2, height: hitArea, background: "#4caf50", pointerEvents: "none" }} />
      )}
      {clipEnd !== null && (
        <div style={{ position: "absolute", left: `${(((currentOffset || 0) + clipEnd) / (cumulativeDuration || duration || 1)) * 100}%`, top: 0, width: 2, height: hitArea, background: "#f44336", pointerEvents: "none" }} />
      )}
      {/* Segment boundary tick marks — visual cue for where Q1 ends and Q2 begins */}
      {isMultiSegment && cumulativeDuration > 0 && segmentOffsets.slice(1).map((off, i) => (
        <div key={i} style={{ position: "absolute", left: `${(off / cumulativeDuration) * 100}%`, top: '25%', height: '50%', width: 1, background: 'rgba(255,255,255,0.45)', pointerEvents: "none" }} />
      ))}
    </div>
  );

  // ── Layout ──
  // Container is always position:relative. The video lives inside a host div
  // that exists at the SAME JSX position whether we're portrait or fullscreen
  // — only its style props change. So the underlying DOM <video> element
  // never gets unmounted, and currentTime/buffered ranges/orange bar stay
  // intact across orientation toggles.
  return (
    <div
      ref={containerRef}
      data-testid="video-player-container"
      style={{
        background: "#000",
        borderRadius: isFullscreen ? 0 : 12,
        overflow: "hidden",
        position: isCssFullscreen ? "fixed" : "relative",
        inset: isCssFullscreen ? 0 : undefined,
        zIndex: isCssFullscreen ? 9999 : undefined,
        width: "100%",
        height: isCssFullscreen ? "100vh" : undefined,
      }}
      onMouseMove={resetHideTimer}
    >
      {/* ─── PORTRAIT TOP BAR — only when not fullscreen ─── */}
      {!isFullscreen && (
        <div data-testid="player-top-bar" style={{
          display: "flex", alignItems: "center", justifyContent: "space-between",
          padding: "12px 16px", background: "rgba(0,0,0,0.6)", position: "relative", zIndex: 10,
        }}>
          <button data-testid="player-back-btn" onClick={onBack} style={{
            width: 40, height: 40, borderRadius: "50%", background: "rgba(255,255,255,0.1)",
            border: "none", color: "#fff", cursor: "pointer", display: "flex",
            alignItems: "center", justifyContent: "center",
          }}><ArrowLeft size={20} /></button>
          <h2 data-testid="player-title" style={{ fontSize: 15, fontWeight: 600, color: "#fff", flex: 1, textAlign: "center", margin: "0 12px" }}>
            {game?.title}
          </h2>
          <button data-testid="player-fullscreen-btn" onClick={toggleFullscreen} style={{
            width: 40, height: 40, borderRadius: "50%", background: "rgba(255,255,255,0.1)",
            border: "none", color: "#fff", cursor: "pointer", display: "flex",
            alignItems: "center", justifyContent: "center",
          }}><Maximize2 size={18} /></button>
        </div>
      )}

      {/* ─── SEGMENT TABS (multi-part games only) ─── */}
      {!isFullscreen && isMultiSegment && (
        <div data-testid="segment-tabs" style={{
          display: "flex", gap: 6, padding: "8px 12px", overflowX: "auto",
          background: "rgba(0,0,0,0.45)", borderBottom: "1px solid rgba(255,255,255,0.06)",
          scrollbarWidth: "none", msOverflowStyle: "none",
        }}>
          {segments.map((s, idx) => {
            const active = idx === segmentIdx;
            return (
              <button
                key={s.id || idx}
                data-testid={`segment-tab-${idx}`}
                onClick={() => switchToSegment(idx)}
                style={{
                  flexShrink: 0, padding: "8px 14px", cursor: "pointer",
                  background: active ? "var(--cyan)" : "rgba(255,255,255,0.05)",
                  border: "1px solid " + (active ? "var(--cyan)" : "rgba(255,255,255,0.1)"),
                  borderRadius: 999, color: active ? "#000" : "#fff",
                  fontSize: 12, fontWeight: 800,
                  fontFamily: "'DM Sans', sans-serif",
                  transition: "all 0.15s ease",
                }}
              >
                {s.label}
                {active && s.duration_seconds ? <span style={{ marginLeft: 6, opacity: 0.7, fontWeight: 600 }}>· {formatTime(s.duration_seconds)}</span> : null}
              </button>
            );
          })}
        </div>
      )}

      {/* ─── THE ONE AND ONLY VIDEO HOST ───
          Same JSX position, only its style changes. */}
      <div
        data-testid="video-host"
        style={isFullscreen
          ? { position: "absolute", inset: 0, background: "#000", zIndex: 0 }
          : { position: "relative", width: "100%", aspectRatio: "16/9", maxHeight: "calc(100vh - 380px)", overflow: "hidden", background: "#000" }
        }
      >
        <video
          ref={videoRef}
          data-testid="video-element"
          poster={game?.thumbnail_url || undefined}
          preload="metadata"
          playsInline
          onError={() => setVideoError(true)}
          onLoadedMetadata={() => setVideoError(false)}
          onEnded={() => {
            // Auto-advance to next segment when one ends — keeps the game
            // playing continuously without the user clicking the next tab.
            if (segmentIdx < segments.length - 1) {
              wasPlayingRef.current = true;
              setSegmentIdx(idx => idx + 1);
            } else {
              setPlaying(false);
            }
          }}
          style={{
            width: "100%", height: "100%", objectFit: "contain", display: "block",
            background: "#000",
            transform: `scale(${zoom})`, transformOrigin: "center center",
            transition: "transform 0.2s ease",
          }}
        >
          {currentVideoUrl && (
            <source src={currentVideoUrl} type={currentVideoUrl.endsWith(".webm") ? "video/webm" : "video/mp4"} />
          )}
          {currentVideoUrl && currentVideoUrl.endsWith(".mp4") && (
            <source src={currentVideoUrl.replace(/\.mp4$/, ".webm")} type="video/webm" />
          )}
        </video>

        {videoError && (
          <div data-testid="video-error" style={{
            position: "absolute", inset: 0, display: "flex", flexDirection: "column",
            alignItems: "center", justifyContent: "center", gap: 12,
            background: "rgba(0,0,0,0.85)", color: "#fff", padding: 20, textAlign: "center",
          }}>
            <p style={{ fontSize: 15, fontWeight: 600, color: "#ff6b00" }}>Video unavailable</p>
            <p style={{ fontSize: 12, color: "#aaa", maxWidth: 320 }}>The uploaded file couldn't be played. It may still be processing, the URL could be invalid, or the format isn't supported.</p>
            <p style={{ fontSize: 11, color: "#666", wordBreak: "break-all", maxWidth: 360 }}>{game?.video_url}</p>
          </div>
        )}

        {/* YSV watermark + zoom — always rendered inside host. Sized roughly
            1.7× the original — visible enough to identify the platform without
            getting in the way of the game footage. */}
        <div data-testid="ysv-watermark" style={{
          position: "absolute", bottom: 14, left: 14, display: "flex", alignItems: "center", gap: 6, opacity: 0.75,
          pointerEvents: "none",
        }}>
          <div style={{ width: 34, height: 34, borderRadius: 7, border: "2px solid var(--cyan)", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <Play size={18} fill="var(--cyan)" style={{ color: "var(--cyan)", marginLeft: 2 }} />
          </div>
          <span style={{ color: "var(--cyan)", fontSize: 24, fontWeight: 800, fontFamily: "'Outfit', sans-serif", letterSpacing: 0.5 }}>YSV</span>
        </div>
        <div data-testid="zoom-controls" style={{
          position: "absolute",
          // In fullscreen, push the controls in from the safe area + give
          // them space below the top header bar so they're never clipped by
          // the dynamic island / camera notch.
          top: isFullscreen ? "calc(env(safe-area-inset-top, 0px) + 70px)" : 8,
          right: isFullscreen ? "calc(env(safe-area-inset-right, 0px) + 16px)" : 8,
          display: "flex", flexDirection: isFullscreen ? "column" : "row", gap: 6,
          zIndex: 12,
        }}>
          <button data-testid="zoom-in-btn" onClick={(e) => { e.stopPropagation(); zoomIn(); }} style={{
            width: isFullscreen ? 40 : 32, height: isFullscreen ? 40 : 32,
            borderRadius: 6, background: "rgba(0,0,0,0.7)", border: "1px solid rgba(255,255,255,0.2)",
            color: "#fff", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center",
          }}><ZoomIn size={isFullscreen ? 18 : 16} /></button>
          <button data-testid="zoom-out-btn" onClick={(e) => { e.stopPropagation(); zoomOut(); }} style={{
            width: isFullscreen ? 40 : 32, height: isFullscreen ? 40 : 32,
            borderRadius: 6, background: "rgba(0,0,0,0.7)", border: "1px solid rgba(255,255,255,0.2)",
            color: "#fff", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center",
          }}><ZoomOut size={isFullscreen ? 18 : 16} /></button>
          {zoom > 1 && <span style={{ color: "#fff", fontSize: 11, background: "rgba(0,0,0,0.7)", padding: "4px 8px", borderRadius: 4, alignSelf: "center" }}>{zoom.toFixed(2)}x</span>}
        </div>

        {/* ─── Fullscreen overlay UI lives INSIDE the host so it sits on top
              of the video and tracks its size. ─── */}
        {isFullscreen && (
          <div
            data-testid="landscape-mode"
            onClick={() => {
              // Tap-to-toggle: when controls are auto-hidden (4s after play
              // started) tapping the video brings the scrub bar / buttons back.
              // Without this the bar was invisible on mobile fullscreen with no
              // way to recover (mousemove doesn't fire on touch).
              if (!showControls) {
                setShowControls(true);
                resetHideTimer();
              } else {
                // Quick-tap while controls are visible hides them so the user
                // can watch the game uncluttered.
                if (hideTimer.current) clearTimeout(hideTimer.current);
                setShowControls(false);
              }
            }}
            style={{ position: "absolute", inset: 0, cursor: showControls ? "default" : "pointer" }}>
            <button data-testid="fs-always-exit-btn" onClick={(e) => { e.stopPropagation(); toggleFullscreen(); }} style={{
              position: "absolute", top: 16, left: 16, zIndex: 11,
              width: 44, height: 44, borderRadius: "50%",
              background: "rgba(0,0,0,0.6)", border: "1px solid rgba(255,255,255,0.15)",
              color: "#fff", cursor: "pointer",
              display: "flex", alignItems: "center", justifyContent: "center",
            }} title="Exit full screen (Esc)"><Minimize2 size={20} /></button>

            <div
              onClick={(e) => e.stopPropagation()}
              style={{
                position: "absolute", inset: 0, display: "flex", flexDirection: "column",
                justifyContent: "space-between", pointerEvents: showControls ? "auto" : "none",
                opacity: showControls ? 1 : 0, transition: "opacity 0.3s ease",
              }}>
              <div style={{ display: "flex", alignItems: "center", padding: "16px 24px 16px 76px", background: "linear-gradient(180deg, rgba(0,0,0,0.7), transparent)" }}>
                <h2 style={{ flex: 1, textAlign: "center", fontSize: 16, fontWeight: 600, color: "#fff" }}>{game?.title}</h2>
              </div>

              <div style={{ background: "linear-gradient(0deg, rgba(0,0,0,0.8), transparent)", padding: "40px 24px 16px" }}>
                <div style={{ display: "flex", alignItems: "flex-end", justifyContent: "flex-end", marginBottom: 12, gap: 16 }}>
                  <div style={{ display: "flex", gap: 12 }}>
                    <button data-testid="fs-start-clip" onClick={setClipStartMark} style={{ background: "none", border: "none", cursor: "pointer" }}>
                      <Flag size={22} style={{ color: "#4caf50" }} fill="#4caf50" />
                    </button>
                    <button data-testid="fs-end-clip" onClick={setClipEndMark} style={{ background: "none", border: "none", cursor: "pointer" }}>
                      <Flag size={22} style={{ color: "#f44336" }} fill="#f44336" />
                    </button>
                  </div>
                  <button data-testid="fs-clips-icon" style={{ background: "none", border: "none", cursor: "pointer", color: "#fff" }}>
                    <Scissors size={20} />
                  </button>
                </div>

                <div style={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 16, marginBottom: 12 }}>
                  <button onClick={() => skip(-30)} style={{ background: "none", border: "none", color: "#fff", cursor: "pointer" }}><Rewind size={22} /></button>
                  <button onClick={() => skip(-5)} style={{ background: "none", border: "none", color: "#fff", cursor: "pointer" }}><SkipBack size={22} /></button>
                  <button data-testid="fs-play" onClick={togglePlay} style={{
                    width: 52, height: 52, borderRadius: "50%", background: "#ff6b00",
                    border: "none", color: "#fff", cursor: "pointer", display: "flex",
                    alignItems: "center", justifyContent: "center",
                  }}>
                    {playing ? <Pause size={22} fill="#fff" /> : <Play size={22} fill="#fff" style={{ marginLeft: 2 }} />}
                  </button>
                  <button onClick={() => skip(5)} style={{ background: "none", border: "none", color: "#fff", cursor: "pointer" }}><SkipForward size={22} /></button>
                  <button onClick={() => skip(30)} style={{ background: "none", border: "none", color: "#fff", cursor: "pointer" }}><FastForward size={22} /></button>
                </div>

                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <span ref={fsTimeLabelRef} data-testid="fs-time-current" style={{ color: "#fff", fontSize: 12, fontFamily: "monospace", minWidth: 36 }}>0:00</span>
                  <ProgressBar height={5} thumbSize={16} hitArea={18} fillRefHook={fsFillRef} thumbRefHook={fsThumbRef} />
                  <span data-testid="fs-time-total" style={{ color: "#fff", fontSize: 12, fontFamily: "monospace", minWidth: 36 }}>{formatTime(cumulativeDuration > 0 ? cumulativeDuration : duration)}</span>
                </div>
              </div>
            </div>

            {/* ─── Centered floating clip-save panel — fullscreen/landscape only.
                  Fires when the user hits "End Clip" while watching in landscape.
                  Video keeps playing underneath (no pause), so the moment of the
                  game stays visible while titling the clip. ─── */}
            {showClipSave && (
              <div
                data-testid="fs-save-clip-overlay"
                onClick={(e) => e.stopPropagation()}
                style={{
                  position: "absolute", top: "50%", left: "50%", transform: "translate(-50%, -50%)",
                  zIndex: 25, width: "min(420px, 80%)",
                  background: "rgba(15,17,23,0.95)", border: "1.5px solid var(--cyan)",
                  borderRadius: 14, padding: "18px 18px 16px",
                  boxShadow: "0 10px 40px rgba(0,0,0,0.6)",
                  backdropFilter: "blur(8px)",
                }}>
                <h4 style={{ fontSize: 14, fontWeight: 700, color: "var(--cyan)", marginBottom: 4, fontFamily: "'Outfit', sans-serif", letterSpacing: 0.5, textTransform: 'uppercase' }}>
                  Save Clip
                </h4>
                <p style={{ fontSize: 11, color: "#bfc7d4", marginBottom: 12 }}>
                  {clipStart !== null && clipEnd !== null
                    ? `${formatTime(clipStart)} → ${formatTime(clipEnd)} · ${Math.max(0, (clipEnd - clipStart)).toFixed(1)}s`
                    : "Mark a start time first"}
                </p>
                <input
                  data-testid="fs-clip-title-input"
                  className="input-field"
                  placeholder="Clip title"
                  value={clipTitle}
                  autoFocus
                  onChange={(e) => setClipTitle(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && clipTitle.trim()) saveClip();
                    if (e.key === "Escape") setShowClipSave(false);
                  }}
                  style={{ marginBottom: 12, background: "#0d1117", color: "#fff" }}
                />
                <div style={{ display: "flex", gap: 8 }}>
                  <button
                    data-testid="fs-save-clip-confirm"
                    onClick={saveClip}
                    disabled={!clipTitle.trim() || clipStart === null || clipEnd === null}
                    style={{
                      flex: 1, padding: "10px", background: clipTitle.trim() ? "#ff6b00" : "rgba(255,107,0,0.35)",
                      border: "none", borderRadius: 8, color: "#fff", fontWeight: 800, fontSize: 13,
                      cursor: clipTitle.trim() ? "pointer" : "not-allowed", fontFamily: "'Outfit', sans-serif",
                    }}>Save Clip</button>
                  <button
                    data-testid="fs-save-clip-cancel"
                    onClick={() => setShowClipSave(false)}
                    style={{
                      padding: "10px 16px", background: "transparent",
                      border: "1px solid rgba(255,255,255,0.25)", borderRadius: 8,
                      color: "#fff", fontWeight: 700, fontSize: 13, cursor: "pointer", fontFamily: "'Outfit', sans-serif",
                    }}>Cancel</button>
                </div>
              </div>
            )}

            {/* Toast inside the fullscreen overlay so it floats over the video */}
            {toast && (
              <div data-testid="player-toast" style={{
                position: "absolute", top: 24, left: "50%", transform: "translateX(-50%)",
                background: "rgba(0,0,0,0.85)", color: "#fff",
                padding: "10px 18px", borderRadius: 999, fontSize: 14, fontWeight: 600,
                display: "flex", alignItems: "center", gap: 8,
                border: `1.5px solid ${toast.color}`, zIndex: 20,
              }}>
                <CheckCircle2 size={16} style={{ color: toast.color }} /> {toast.msg}
              </div>
            )}
          </div>
        )}
      </div>

      {/* ─── PORTRAIT BELOW-VIDEO CHROME ─── */}
      {!isFullscreen && (
        <>
          <div data-testid="progress-section" style={{ padding: "12px 16px 4px", display: "flex", alignItems: "center", gap: 10 }}>
            <span ref={timeLabelRef} data-testid="time-current" style={{ color: "#fff", fontSize: 13, fontFamily: "monospace", minWidth: 36 }}>0:00</span>
            <ProgressBar height={5} thumbSize={16} hitArea={18} fillRefHook={fillRef} thumbRefHook={thumbRef} />
            <span data-testid="time-total" style={{ color: "#fff", fontSize: 13, fontFamily: "monospace", minWidth: 36 }}>{formatTime(cumulativeDuration > 0 ? cumulativeDuration : duration)}</span>
          </div>

          <div data-testid="playback-controls" style={{
            display: "flex", alignItems: "center", justifyContent: "center", gap: 20, padding: "8px 16px 16px",
          }}>
            <button data-testid="ctrl-rewind" onClick={() => skip(-30)} style={{ background: "none", border: "none", color: "#fff", cursor: "pointer", textAlign: "center" }}>
              <Rewind size={24} /><p style={{ fontSize: 10, marginTop: 2 }}>Rewind</p>
            </button>
            <button data-testid="ctrl-back5" onClick={() => skip(-5)} style={{ background: "none", border: "none", color: "#fff", cursor: "pointer", textAlign: "center" }}>
              <SkipBack size={24} /><p style={{ fontSize: 10, marginTop: 2 }}>-5s</p>
            </button>
            <button data-testid="ctrl-play" onClick={togglePlay} style={{
              width: 56, height: 56, borderRadius: "50%", background: "#ff6b00",
              border: "none", color: "#fff", cursor: "pointer", display: "flex",
              alignItems: "center", justifyContent: "center",
            }}>
              {playing ? <Pause size={24} fill="#fff" /> : <Play size={24} fill="#fff" style={{ marginLeft: 3 }} />}
            </button>
            <button data-testid="ctrl-fwd5" onClick={() => skip(5)} style={{ background: "none", border: "none", color: "#fff", cursor: "pointer", textAlign: "center" }}>
              <SkipForward size={24} /><p style={{ fontSize: 10, marginTop: 2 }}>+5s</p>
            </button>
            <button data-testid="ctrl-forward" onClick={() => skip(30)} style={{ background: "none", border: "none", color: "#fff", cursor: "pointer", textAlign: "center" }}>
              <FastForward size={24} /><p style={{ fontSize: 10, marginTop: 2 }}>Forward</p>
            </button>
          </div>

          <div data-testid="create-clip-section" style={{ padding: "0 16px 16px" }}>
            <h3 style={{ fontSize: 16, fontWeight: 700, color: "#fff", marginBottom: 10 }}>Create Clip</h3>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
              <button data-testid="start-clip-btn" onClick={setClipStartMark} style={{
                display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
                padding: "14px 16px", background: "#1a2332", border: "1px solid #2a3a4a",
                borderRadius: 8, color: "#fff", cursor: "pointer", fontSize: 14, fontWeight: 600,
                fontFamily: "'DM Sans', sans-serif",
              }}>
                <Flag size={16} style={{ color: "#4caf50" }} fill="#4caf50" /> Start Clip
              </button>
              <button data-testid="end-clip-btn" onClick={setClipEndMark} style={{
                display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
                padding: "14px 16px", background: "#1a2332", border: "1px solid #2a3a4a",
                borderRadius: 8, color: "#fff", cursor: "pointer", fontSize: 14, fontWeight: 600,
                fontFamily: "'DM Sans', sans-serif",
              }}>
                <Flag size={16} style={{ color: "#f44336" }} fill="#f44336" /> End Clip
              </button>
            </div>
            {(clipStart !== null || clipEnd !== null) && (
              <div style={{ display: "flex", gap: 16, marginTop: 8, fontSize: 12, color: "var(--text-secondary)" }}>
                {clipStart !== null && <span>Start: <strong style={{ color: "#4caf50" }}>{formatTime(clipStart)}</strong></span>}
                {clipEnd !== null && <span>End: <strong style={{ color: "#f44336" }}>{formatTime(clipEnd)}</strong></span>}
              </div>
            )}
          </div>

          {showClipSave && (
            <div data-testid="save-clip-dialog" style={{ padding: "0 16px 16px" }}>
              <div style={{ background: "#1a2332", border: "1px solid #2a3a4a", borderRadius: 10, padding: 16 }}>
                <h4 style={{ fontSize: 14, fontWeight: 600, color: "#fff", marginBottom: 10 }}>Save Clip</h4>
                <input data-testid="clip-title-input" className="input-field" placeholder="Clip title" value={clipTitle}
                  onChange={e => setClipTitle(e.target.value)} style={{ marginBottom: 10, background: "#0d1117" }} />
                <div style={{ display: "flex", gap: 8 }}>
                  <button data-testid="save-clip-confirm" onClick={saveClip} style={{
                    flex: 1, padding: "10px", background: "#ff6b00", border: "none", borderRadius: 6,
                    color: "#fff", fontWeight: 600, cursor: "pointer", fontSize: 13, fontFamily: "'DM Sans', sans-serif",
                  }}>Save Clip</button>
                  <button onClick={() => setShowClipSave(false)} style={{
                    padding: "10px 16px", background: "transparent", border: "1px solid #2a3a4a", borderRadius: 6,
                    color: "#fff", cursor: "pointer", fontSize: 13, fontFamily: "'DM Sans', sans-serif",
                  }}>Cancel</button>
                </div>
              </div>
            </div>
          )}

          <div data-testid="game-info" style={{ padding: "8px 16px 24px" }}>
            <h2 style={{ fontSize: 20, fontWeight: 700, color: "#fff", marginBottom: 4 }}>{game?.title}</h2>
            <p style={{ fontSize: 14, color: "#ff6b00", marginBottom: 4 }}>vs {game?.away_team_name || game?.opponent}</p>
            <p style={{ fontSize: 13, color: "#aaa", marginBottom: 2 }}>{game?.description}</p>
            <p style={{ fontSize: 12, color: "#777" }}>Uploaded by {game?.uploaded_by_name}</p>
          </div>

          {/* Portrait toast — separate from fullscreen toast so they don't collide */}
          {toast && (
            <div data-testid="player-toast" style={{
              position: "fixed", bottom: 80, left: "50%", transform: "translateX(-50%)",
              background: "rgba(0,0,0,0.92)", color: "#fff",
              padding: "10px 18px", borderRadius: 999, fontSize: 14, fontWeight: 600,
              display: "flex", alignItems: "center", gap: 8,
              border: `1.5px solid ${toast.color}`, zIndex: 1000,
            }}>
              <CheckCircle2 size={16} style={{ color: toast.color }} /> {toast.msg}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default VideoPlayer;
